import React, { useState, useEffect } from "react";
import { Calendar, dayjsLocalizer } from "react-big-calendar";
import dayjs from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  Paper,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import TodayIcon from "@mui/icons-material/Today";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTheme } from "@mui/material/styles";
import { getAppointmentsCalendar } from "../../../api/appointmentService";
import DateAppointmentsView from "../modals/DateAppointmentsView";

// ✅ Custom Toolbar with centered month/year and arrow icons
function CustomToolbar({ label, onNavigate }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
      }}
    >
      {/* Left navigation */}
      <Box>
        <IconButton onClick={() => onNavigate("PREV")} size="small" color="primary">
          <KeyboardArrowLeftIcon />
        </IconButton>
        <IconButton onClick={() => onNavigate("NEXT")} size="small" color="primary">
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>

      {/* Centered month/year */}
      <Typography
        variant="h6"
        sx={{
          flexGrow: 1,
          textAlign: "center",
          fontWeight: 700,
          color: "primary.main",
        }}
      >
        {label}
      </Typography>

      {/* Right side: Today button */}
      <IconButton
        onClick={() => onNavigate("TODAY")}
        size="small"
        color="primary"
      >
        <Typography variant="caption">TODAY</Typography> <TodayIcon />
      </IconButton>
    </Box>
  );
}

// ✅ Custom Event Renderer (icon instead of text)
function EventIcon() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <AccessTimeIcon sx={{ fontSize: "1rem", color: "white" }} />
    </Box>
  );
}

export default function AppointmentsCalendarDesktop() {
  const localizer = dayjsLocalizer(dayjs);
  const theme = useTheme();

  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().toDate());
  const [viewOpen, setViewOpen] = useState(false);

  useEffect(() => {
    async function fetchAppointments() {
      try {
        const appointments = await getAppointmentsCalendar();
        const mapped = appointments.map((a) => ({
          title: "Appointment", // title is ignored since we override with EventIcon
          start: dayjs(a.date).toDate(),
          end: dayjs(a.date).add(1, "hour").toDate(),
          allDay: true,
        }));
        setEvents(mapped);
      } catch (err) {
        console.error("Failed to fetch appointments", err);
      }
    }
    fetchAppointments();
  }, []);

  const handleSelectEvent = (event) => {
    setSelectedDate(event.start);
    setViewOpen(true);
  };

  const handleSelectSlot = ({ start }) => {
    setSelectedDate(start);
    setViewOpen(true);
  };

  return (
    <>
      <Paper elevation={3}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Clinic Calendar</Typography>
        </Box>

        {/* Calendar */}
        <Box sx={{ p: 2 }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{
              height: 600,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              padding: "8px",
            }}
            selectable
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            defaultDate={selectedDate}
            defaultView="month"   // ✅ force month view
            views={["month"]}     // ✅ only allow month
            components={{
              toolbar: CustomToolbar, // ✅ custom toolbar
              event: EventIcon,       // ✅ custom event renderer
            }}
            eventPropGetter={() => ({
              style: {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.paper,
                borderRadius: "4px",
                padding: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            })}
          />
        </Box>
      </Paper>

      {/* Modal */}
      <DateAppointmentsView
        date={dayjs(selectedDate).format("YYYY-MM-DD")}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
      />
    </>
  );
}
